import { Box, Fade, Grid, Radio, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Email, EmailOutlined, FileCopyOutlined, Send, Sms, SmsOutlined } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { GlobalStore, Layout, LoginLogoImg, useBodyBackgroundColor } from "@roc/feature-app-core";
import { iphoneImage } from "@roc/feature-app-core/assets";
import { Button, NumberFormat, TextField } from "@roc/ui";
import { TextField as MuiTextField } from "@material-ui/core";
import clsx from "clsx";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { renderToString } from 'react-dom/server';

import swal from "sweetalert";
import { useStore } from "../../hooks/useStore";
import { debounceRequest, isNil } from "@roc/feature-utils";
import { RequestIdVerificationStore } from "./stores/requestIdVerificationStore";

const purple = "#652173";
const purpleLight = "#fdf3ff";

const useSmsStyles = makeStyles({
  smsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  phoneScreen: {
    width: "328px",
    height: "670px",
    backgroundImage: `url(${iphoneImage})`,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    position: "relative",
    padding: "24px 24px 0 30px",
  },
  messageBubble: {
    backgroundColor: "#4CAF50",
    color: "white",
    padding: "12px",
    borderRadius: "12px",
    maxWidth: "80%",
    textAlign: "left",
    marginBottom: "16px",
    marginTop: "40px",
  },
});

const SMSPreview = ({ borrowerName, link, tpoName }) => {
  const classes = useSmsStyles();

  return (
    <div className={classes.smsContainer}>
      <div className={classes.phoneScreen}>
        <div className={classes.messageBubble}>
          <p>Hello {borrowerName} 👋,</p>
          <p>{`Please complete the id verification form using the link below.`}</p>
          <p style={{
            wordBreak: 'break-all'
          }}>
            <a target="_blank" rel="noopener noreferrer" href={link} style={{ color: "#fff", textDecoration: "underline" }}>
              {link}
            </a>
          </p>
          <p>- {tpoName}</p>
        </div>
      </div>
    </div>
  );
};

const useEmailStyles = makeStyles({
  emailContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  emailCard: {
    width: "100%",
    backgroundColor: "#444",
    color: "#ddd",
    borderRadius: "32px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
  },
  emailBody: {
    padding: "24px",
  },
  emailContent: {
    backgroundColor: "#fff",
    color: "#111",
    padding: "16px",
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16
  },
  linkStyle: {
    color: "#007BFF",
    textDecoration: "none",
  },
});

export const getRequestIdVerificationSubject = () => {
  return 'Action Required: Complete Your ID Verification';
}

export const getRequestIdVerificationEmailBodyContent = (borrowerName: string, link: string, tpoName: string) => {
  const classes = useEmailStyles();

  return <>
    <p>Hello {borrowerName},</p>
    <br></br>
    <p>{`Please complete the ID verification form using the link below.`}</p>
    <p>{link ? <a target="_blank" rel="noopener noreferrer" href={link} className={classes.linkStyle}>{`Identity Verification Form`}</a>
      : (<strong>{`<your id verification link here>`}</strong>)}</p>
    <br></br>
    <p>Let us know if you need any help!</p>
    <br></br>
    <p>Thank you,</p>
    <p>{tpoName}</p>
  </>
}

const EmailPreview = ({ subject, toEmail, fromEmail, bodyContent }) => {
  const classes = useEmailStyles();

  return (
    <div className={classes.emailContainer}>
      <div className={classes.emailCard}>
        <div className={classes.emailBody}>
          <p><strong>From:</strong> {fromEmail}</p>
          <p><strong>To:</strong> {toEmail}</p>
          <p><strong>Subject:</strong> {subject}</p>
          <br></br>
          <div className={classes.emailContent}>
            <Box><LoginLogoImg imgStyle={{
              maxWidth: 100
            }} /></Box>
            {bodyContent}
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) =>
  createStyles({
    option: {
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(1, 2),
      border: "1px solid #ccc",
      borderRadius: 16,
      cursor: "pointer",
      color: "#111",
      backgroundColor: "#fff",
      '&:hover': {
        backgroundColor: purpleLight,
      }
    },
    option_selected: {
      color: "#FFF",
      backgroundColor: purple,
      '&:hover': {
        backgroundColor: purple,
      }
    },
    swal: {
      borderRadius: 32,
      '& .swal-button--redirect': {
        backgroundColor: theme.palette.primary.main,
        boxShadow: 'none !important',
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        }
      },
      '& .swal-title': {
        color: '#111',
      },
      '& .swal-text': {
        color: '#111',
        textAlign: 'center',
      },
      '& .swal-icon--success__line--long': {
        backgroundColor: '#00b600',
      },
      '& .swal-icon--success__line--tip': {
        backgroundColor: '#00b600',
      },
      '& .swal-icon--success__ring': {
        borderColor: '#64e164',
      },
    }
  })
);

const useFirstNameAutocompleteFieldStyles = makeStyles(theme => ({
  autocomplete: {
    '& .MuiAutocomplete-endAdornment': {
      display: 'none',
    },
  },
}));

export const BorrowerAutocompleteField = observer(
  ({ selectedBorrower, setSelectedBorrower, store, globalStore }: { globalStore : GlobalStore; store: RequestIdVerificationStore; selectedBorrower: BorrowerOption; setSelectedBorrower: (value: BorrowerOption) => void }) => {

    const classes = useFirstNameAutocompleteFieldStyles();
    const options = store.borrowerOptions;

    const fetchBorrowerOptions = debounceRequest((cancelToken, name) =>  {
      if(globalStore?.userFeatures?.displayLeads) {
        store.findBorrowersAndLeadsByName(name, cancelToken)
      } else {
        store.findBorrowersByName(name, cancelToken)
      }
    }
  )

    return (
      <Box className={classes.autocomplete}>
        <Autocomplete
          id={`autocomplete-borrower`}
          inputValue={selectedBorrower?.name}
          onInputChange={(e, value) => {
            if (e?.nativeEvent?.type === 'input') {
              setSelectedBorrower({
                name: value
              });
              fetchBorrowerOptions(value);
            }
          }}
          options={options}
          renderInput={params => (
            <MuiTextField
              variant={'outlined'}
              label={'Borrower'}
              placeholder="Search by name"
              {...params}
            />
          )}
          getOptionLabel={(option: any) => option?.label ?? ''}
          getOptionSelected={(option: any, value) =>
            option.label === value.label
          }
          onChange={async (event, opt) => {
            const data = await store.fetchRecordData(opt.value);
            setSelectedBorrower({
              borrowerId: data.borrowerId,
              name: data.name,
              phone: ((data.phone ?? '') as string).replace(/\D/g, ''),
              email: data.email,
              firstname: data.firstname,
              lastname: data.lastname
            });
          }}
          fullWidth
          PopperComponent={options.length ? undefined : () => null}
        />
      </Box>
    );
  }
);

interface BorrowerOption {
  name?: string;
  phone?: string;
  email?: string;
  borrowerId?: number;
  firstname?: string;
  lastname?: string;
}

export const RequestIdVerification = observer(() => {
  const classes = useStyles();
  const { requestIdVerificationStore, globalStore } = useStore();
  const { messageConfig } = requestIdVerificationStore;
  const [channel, setChannel] = useState<undefined | 'EMAIL' | 'SMS'>('EMAIL');
  const [borrowerMode, setBorrowerMode] = useState<undefined | 'EXISTING' | 'NEW'>('EXISTING');
  const [selectedBorrower, setSelectedBorrower] = useState<BorrowerOption>({});
  const [fullName, setFullName] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [cellPhone, setCellPhone] = useState<string>('');
  const [borrowerId, setBorrowerId] = useState<number>(null);
  const [firstname, setFirstName] = useState<string>('');
  const [lastname, setLastName] = useState<string>('');

  useBodyBackgroundColor("#fff");

  useEffect(() => {
    if (borrowerMode) {
      if (borrowerMode == 'NEW') {
        setSelectedBorrower(undefined);
        setEmailAddress('');
        setCellPhone('');
        setFullName('');
        setBorrowerId(null);
        setFirstName('');
        setLastName('');
      }
      else {
        setEmailAddress(selectedBorrower?.email ?? '');
        setCellPhone(selectedBorrower?.phone ?? '');
        setFullName(selectedBorrower?.name ?? '');
        setBorrowerId(selectedBorrower?.borrowerId ?? null);
        setFirstName(selectedBorrower?.firstname ?? '');
        setLastName(selectedBorrower?.lastname ?? '');
      }
    }
  }, [borrowerMode, selectedBorrower]);

  useEffect(() => {
    if (!isNil(borrowerId)) {
      getMessageConfig();
    }
  }, [borrowerId]);

  const getMessageConfig = async () => {
    await requestIdVerificationStore.getMessageConfig(borrowerId);
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(messageConfig?.link);
    requestIdVerificationStore?.globalStore?.notificationStore.showSuccessNotification({
      message: 'Copied to clipboard',
    });
  };

  const subject = getRequestIdVerificationSubject();
  const bodyContent = getRequestIdVerificationEmailBodyContent(fullName, null, messageConfig?.tpoName);
  const bodyContentWithLink = getRequestIdVerificationEmailBodyContent(fullName, messageConfig?.link, messageConfig?.tpoName);

  const handleSendEmail = (callback?: () => void) => {
    const emailBodyContent = renderToString(bodyContentWithLink);
    requestIdVerificationStore.sendRequestIdVerificationEmail(emailAddress, subject, emailBodyContent, callback);
  }

  return (
    <Layout maxWidth={'lg'}>
      <Box my={4} textAlign={'left'}>
        <Typography variant={'h3'}>
          Request ID Verification
        </Typography>
      </Box>

      {/* <Box mb={4} fontSize={24}>
        How would you like to send the id verification link to the borrower? Choose an option below:
      </Box> */}

      <Box mb={4}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={5}>
            {/* <Box mb={4} fontSize={18} display={"flex"} gridGap={16} maxWidth={500}>
              <Box
                onClick={() => setChannel('EMAIL')}
                className={clsx(classes.option, channel == "EMAIL" ? classes.option_selected : '')}>
                <Radio
                  checked={channel == "EMAIL"}
                  onChange={() => { }}
                  value={channel}
                  name="email-radio"
                  style={{
                    color: channel == "EMAIL" ? purpleLight : '#777'
                  }}
                />
                {channel == "EMAIL" ? <Email /> : <EmailOutlined />}
                <Box component={"span"} ml={1}>Send via Email</Box>
              </Box>
              <Box
                onClick={() => setChannel('SMS')}
                className={clsx(classes.option, channel == "SMS" ? classes.option_selected : '')}>
                <Radio
                  checked={channel == "SMS"}
                  onChange={() => { }}
                  value={channel}
                  name="email-sms"
                  style={{
                    color: channel == "SMS" ? purpleLight : '#777'
                  }}
                />
                {channel == "SMS" ? <Sms /> : <SmsOutlined />}
                <Box component={"span"} ml={1}>Send via SMS</Box>
              </Box>
            </Box> */}
            <Fade in={channel != undefined}>
              <div>
                {
                  channel &&
                  <>
                    <Box mb={4} fontSize={24}>
                      Please choose an existing borrower
                    </Box>
                    {/* <Box mb={4} fontSize={18} display={"flex"} gridGap={16} maxWidth={500}>
                      <Box
                        onClick={() => setBorrowerMode('EXISTING')}
                        className={clsx(classes.option, borrowerMode == "EXISTING" ? classes.option_selected : '')}>
                        <Radio
                          checked={borrowerMode == "EXISTING"}
                          onChange={() => { }}
                          value={borrowerMode}
                          name="existing-borrower-radio"
                          style={{
                            color: borrowerMode == "EXISTING" ? purpleLight : '#777'
                          }}
                        />
                        <Box component={"span"} ml={1}>Existing Borrower</Box>
                      </Box>
                      <Box
                        onClick={() => setBorrowerMode('NEW')}
                        className={clsx(classes.option, borrowerMode == "NEW" ? classes.option_selected : '')}>
                        <Radio
                          checked={borrowerMode == "NEW"}
                          onChange={() => { }}
                          value={borrowerMode}
                          name="existing-borrower-radio"
                          style={{
                            color: borrowerMode == "NEW" ? purpleLight : '#777'
                          }}
                        />
                        <Box component={"span"} ml={1}>New Borrower</Box>
                      </Box>
                    </Box> */}
                    <Fade in={channel != undefined}>
                      <div>
                        {
                          borrowerMode &&
                          <>
                            {
                              borrowerMode == 'EXISTING' ?
                                <Box mb={4} fontSize={32}>
                                  <BorrowerAutocompleteField
                                    selectedBorrower={selectedBorrower}
                                    setSelectedBorrower={setSelectedBorrower}
                                    store={requestIdVerificationStore}
                                    globalStore={globalStore}
                                  />
                                </Box> :
                                <Box mb={4} fontSize={32}>
                                  <TextField
                                    testId="fullName"
                                    required
                                    value={fullName}
                                    variant="outlined"
                                    label="Name"
                                    onChange={e => {
                                      setFullName(e.target.value);
                                    }}
                                    fullWidth
                                  />
                                </Box>
                            }
                            {
                              channel == "EMAIL" &&
                              <Box fontSize={32}>
                                <TextField
                                  testId="emailAddress"
                                  required
                                  value={emailAddress}
                                  variant="outlined"
                                  label="Email Address"
                                  onChange={e => {
                                    setEmailAddress(e.target.value);
                                  }}
                                  fullWidth
                                  disabled={borrowerMode === 'EXISTING' && !!selectedBorrower?.email}
                                />
                              </Box>
                            }
                            {
                              channel == "SMS" &&
                              <Box fontSize={32}>
                                <NumberFormat
                                  testId="cellPhone"
                                  customInput={TextField}
                                  format="(###) ###-####"
                                  mask="_"
                                  variant="outlined"
                                  label="Cell Phone"
                                  fullWidth
                                  required={true}
                                  value={cellPhone}
                                  onChange={e => {
                                    setCellPhone(e.target.value);
                                  }}
                                />
                              </Box>
                            }
                            <Box my={3} fontSize={32}>
                              <Button testId="send" variant='contained' color='primary' endIcon={<Send />} size='large'
                                disabled={(fullName && (emailAddress || cellPhone)) ? false : true}
                                onClick={() => {
                                  handleSendEmail(() => {
                                    swal({
                                      title: "Success!",
                                      text: `The link has been sent successfully. Please ask the borrower to check their message and complete the process.`,
                                      icon: "success",
                                      buttons: {
                                        redirect: {
                                          text: "Ok",
                                        },
                                      },
                                      className: classes.swal,
                                      closeOnEsc: false,
                                      closeOnClickOutside: false,
                                    }).then(async (v) => {
                                      setFullName('');
                                      setEmailAddress('');
                                      setCellPhone('');
                                      setSelectedBorrower({});
                                      setBorrowerId(undefined);
                                      requestIdVerificationStore.reset();
                                    });
                                  })
                                }}
                                style={{
                                  padding: '16px 24px'
                                }}>
                                {channel == "EMAIL" ? "Send Via Email" : "Send"}
                              </Button>
                            </Box>
                          </>
                        }
                      </div>
                    </Fade>
                  </>
                }
              </div>
            </Fade>
          </Grid>
          {
            channel && borrowerMode &&

            <Grid item xs={12} lg={7}>
              <Box px={5} width={"100%"}>
                <Box display={'flex'} justifyContent={'center'} fontSize={24} my={3} fontWeight={"bold"}>
                  {channel == 'SMS' ? 'Preview' : 'Preview'}
                </Box>
                {
                  channel == 'SMS' && <SMSPreview borrowerName={fullName} link={messageConfig?.link} tpoName={messageConfig?.tpoName} />
                }
                {
                  channel == 'EMAIL' && <EmailPreview subject={subject} toEmail={emailAddress} fromEmail={messageConfig?.fromEmail} bodyContent={bodyContent} />
                }
              </Box>
            </Grid>
          }
        </Grid>
      </Box>

    </Layout>
  )

});
